import { create } from 'zustand';
import type { EditorSession } from '../api/client';
import {
  listThemeFiles,
  readThemeFile,
  writeThemeFile,
  deleteThemeFile,
  downloadThemeFile,
  downloadThemeArchive,
  fetchCustomizedFiles,
} from '../api/client';

/**
 * Code mode state: open tabs, per-tab buffer + dirty flag, save orchestration.
 * Kept separate from the visual editor store so both modes can live on the
 * same screen without stepping on each other.
 */

export interface Tab {
  path: string;          // e.g. "sections/header.liquid"
  original: string;      // content as last loaded/saved
  buffer: string;        // current editor content
  loading: boolean;
  error: string | null;
}

export interface FileSaveStatus {
  state: 'idle' | 'saving' | 'saved' | 'error';
  message?: string;
  at?: number;
}

interface CodeState {
  session: EditorSession | null;

  files: string[];          // all editable paths
  filesLoading: boolean;
  filesError: string | null;

  tabs: Record<string, Tab>;
  tabOrder: string[];
  activePath: string | null;

  /**
   * Paths the tenant has modified (manually or via Luna). Shown in the
   * FileTree as a subtle badge so the user knows which files survive a
   * theme-sync upgrade. Populated from /customized-files on init + after
   * every successful save.
   */
  customized: Set<string>;

  saveStatus: FileSaveStatus;

  // actions
  init(session: EditorSession): Promise<void>;
  refreshFiles(): Promise<void>;
  refreshCustomized(): Promise<void>;
  openFile(path: string): Promise<void>;
  closeTab(path: string): void;
  activate(path: string): void;
  updateBuffer(path: string, next: string): void;
  saveActive(): Promise<void>;
  saveAll(): Promise<void>;
  /**
   * Reloads buffer content for files modified externally (e.g., by Luna AI
   * applying a proposal). Called from luna-store após apply pra evitar
   * que o usuário sobrescreva mudanças da AI ao salvar um buffer stale.
   *
   * Estratégia:
   *  - Se a tab tem mudanças não-salvas (buffer !== original) → mantém,
   *    avisa que ficou desatualizada (tab.externallyModified = true).
   *  - Se a tab é "clean" (buffer === original) → re-fetch o conteúdo
   *    novo e atualiza ambos buffer e original. User vê edição da AI
   *    refletida sem perder nada.
   */
  refreshBufferIfOpen(path: string): Promise<void>;

  /** Cria arquivo novo. Boilerplate por extensão se `content` omitido. */
  createFile(path: string, content?: string): Promise<void>;
  /** Apaga arquivo do S3 + fecha tab aberta + tira do customized set. */
  deleteFile(path: string): Promise<void>;
  /** Rename via write-new + delete-old. Migra tab preservando dirty. */
  renameFile(from: string, to: string): Promise<void>;
  /** Upload de File binário/texto. `destPathOrFolder` aceita ambos. */
  uploadFile(file: File, destPathOrFolder: string): Promise<void>;
  /** Download de arquivo único (raw content, sem zip). */
  downloadFile(path: string): Promise<void>;
  /** Download de pasta (ou tema inteiro se `prefix` vazio) como ZIP. */
  downloadFolder(prefix: string): Promise<void>;
}

export const useCode = create<CodeState>((set, get) => ({
  session: null,
  files: [],
  filesLoading: false,
  filesError: null,
  tabs: {},
  tabOrder: [],
  activePath: null,
  customized: new Set<string>(),
  saveStatus: { state: 'idle' },

  async init(session) {
    set({ session });
    await Promise.all([get().refreshFiles(), get().refreshCustomized()]);
  },

  async refreshFiles() {
    const { session } = get();
    if (!session) return;
    set({ filesLoading: true, filesError: null });
    try {
      const files = await listThemeFiles(session);
      set({ files, filesLoading: false });
    } catch (err: any) {
      set({ filesError: err.message, filesLoading: false });
    }
  },

  async refreshCustomized() {
    const { session } = get();
    if (!session) return;
    try {
      const files = await fetchCustomizedFiles(session);
      set({ customized: new Set(files) });
    } catch (err) {
      // Non-fatal: if the endpoint isn't there, the FileTree simply
      // won't show badges. No reason to surface an error.
      console.warn('[code] customized-files fetch failed', err);
    }
  },

  async openFile(path) {
    const { session, tabs } = get();
    if (!session) return;

    // Already open → just activate
    if (tabs[path]) {
      set({ activePath: path });
      return;
    }

    // Insert placeholder tab
    set((s) => ({
      tabs: {
        ...s.tabs,
        [path]: { path, original: '', buffer: '', loading: true, error: null },
      },
      tabOrder: s.tabOrder.includes(path) ? s.tabOrder : [...s.tabOrder, path],
      activePath: path,
    }));

    try {
      const content = await readThemeFile(session, path);
      set((s) => ({
        tabs: {
          ...s.tabs,
          [path]: { path, original: content, buffer: content, loading: false, error: null },
        },
      }));
    } catch (err: any) {
      set((s) => ({
        tabs: {
          ...s.tabs,
          [path]: { path, original: '', buffer: '', loading: false, error: err.message },
        },
      }));
    }
  },

  closeTab(path) {
    set((s) => {
      const { [path]: _drop, ...rest } = s.tabs;
      const order = s.tabOrder.filter((p) => p !== path);
      const active =
        s.activePath === path
          ? order[order.length - 1] || null
          : s.activePath;
      return { tabs: rest, tabOrder: order, activePath: active };
    });
  },

  activate(path) {
    set({ activePath: path });
  },

  updateBuffer(path, next) {
    set((s) => {
      const tab = s.tabs[path];
      if (!tab) return s;
      return {
        tabs: { ...s.tabs, [path]: { ...tab, buffer: next } },
      };
    });
  },

  async saveActive() {
    const { session, activePath, tabs } = get();
    if (!session || !activePath) return;
    const tab = tabs[activePath];
    if (!tab || tab.buffer === tab.original) return;

    set({ saveStatus: { state: 'saving' } });
    try {
      await writeThemeFile(session, activePath, tab.buffer);
      set((s) => ({
        tabs: {
          ...s.tabs,
          [activePath]: { ...s.tabs[activePath], original: tab.buffer },
        },
        customized: new Set([...s.customized, activePath]),
        saveStatus: { state: 'saved', at: Date.now() },
      }));
    } catch (err: any) {
      set({ saveStatus: { state: 'error', message: err.message } });
    }
  },

  async refreshBufferIfOpen(path) {
    const { session, tabs } = get();
    if (!session) return;
    const tab = tabs[path];
    if (!tab) return; // não aberto, nada a fazer

    try {
      const content = await readThemeFile(session, path);

      const hasUnsavedEdits = tab.buffer !== tab.original;
      if (hasUnsavedEdits) {
        /*
          Tab tem edits não-salvos. Não pode sobrescrever o buffer
          (perderia trabalho do user). Atualiza só `original` pra
          marcar o novo baseline + flag `externallyModified` pra UI
          mostrar aviso "Esse arquivo foi modificado externamente,
          seu save vai sobrescrever as mudanças."
        */
        set((s) => ({
          tabs: {
            ...s.tabs,
            [path]: {
              ...s.tabs[path],
              original: content,
              externallyModified: true,
            },
          },
        }));
      } else {
        /*
          Tab limpa. Pode atualizar buffer + original com o conteúdo
          novo. User vê a edição da AI imediatamente.
        */
        set((s) => ({
          tabs: {
            ...s.tabs,
            [path]: {
              ...s.tabs[path],
              buffer: content,
              original: content,
              externallyModified: false,
            },
          },
        }));
      }
    } catch (err: any) {
      console.warn('[code] refreshBufferIfOpen failed for', path, err);
    }
  },

  async createFile(path, content) {
    const { session, files } = get();
    if (!session) throw new Error('Sessão expirada. Recarregue o editor.');
    const clean = String(path).replace(/^\/+/, '').replace(/\\/g, '/');
    if (!clean) throw new Error('Caminho vazio.');
    if (files.includes(clean)) {
      throw new Error(`Já existe um arquivo em "${clean}".`);
    }
    const body = content ?? boilerplateFor(clean);
    await writeThemeFile(session, clean, body);
    await get().refreshFiles();
    await get().openFile(clean);
  },

  async deleteFile(path) {
    const { session, tabs, tabOrder, activePath, customized } = get();
    if (!session) throw new Error('Sessão expirada.');
    await deleteThemeFile(session, path);

    // Fecha tab se aberta + tira da customized set
    const nextTabs = { ...tabs };
    delete nextTabs[path];
    const nextOrder = tabOrder.filter((p) => p !== path);
    const nextActive =
      activePath === path ? (nextOrder[0] || null) : activePath;
    const nextCustomized = new Set(customized);
    nextCustomized.delete(path);
    set({
      tabs: nextTabs,
      tabOrder: nextOrder,
      activePath: nextActive,
      customized: nextCustomized,
    });
    await get().refreshFiles();
  },

  async renameFile(from, to) {
    const { session } = get();
    if (!session) throw new Error('Sessão expirada.');
    const cleanTo = String(to).replace(/^\/+/, '').replace(/\\/g, '/');
    if (from === cleanTo) return;
    const content = await readThemeFile(session, from);
    // Cria no novo path ANTES de deletar o antigo — se cair no meio,
    // o user fica com os dois em vez de perder o conteúdo.
    await writeThemeFile(session, cleanTo, content);
    await deleteThemeFile(session, from);

    // Se tava aberto, migra a tab preservando dirty state
    const { tabs, tabOrder, activePath } = get();
    if (tabs[from]) {
      const migrated = { ...tabs[from], path: cleanTo };
      const nextTabs = { ...tabs, [cleanTo]: migrated };
      delete nextTabs[from];
      const nextOrder = tabOrder.map((p) => (p === from ? cleanTo : p));
      const nextActive = activePath === from ? cleanTo : activePath;
      set({ tabs: nextTabs, tabOrder: nextOrder, activePath: nextActive });
    }
    await get().refreshFiles();
  },

  async uploadFile(file, destPathOrFolder) {
    const { session } = get();
    if (!session) throw new Error('Sessão expirada.');

    // Se termina com "/" ou não tem extensão, é FOLDER — usa file.name
    const target = (() => {
      const p = String(destPathOrFolder).replace(/^\/+/, '').replace(/\\/g, '/');
      const hasExt = /\.[a-z0-9]+$/i.test(p);
      if (hasExt) return p;
      const folder = p.replace(/\/+$/, '');
      return folder ? `${folder}/${file.name}` : file.name;
    })();

    /*
      writeThemeFile aceita string. Pra binários (imagens em assets/),
      lemos como base64 e enviamos assim — o engine reconhece o
      content-type pela extensão no writeThemeFile e escreve raw.

      Text files (.liquid, .css, .json, .svg, .txt, .js) vão como
      texto puro.
    */
    const isText = /\.(liquid|json|css|scss|js|ts|html|svg|txt|md|xml|yml|yaml)$/i.test(target);
    const content = isText ? await file.text() : await fileToBase64(file);

    await writeThemeFile(session, target, content);
    await get().refreshFiles();
  },

  async downloadFile(path) {
    const { session } = get();
    if (!session) throw new Error('Sessão expirada.');
    await downloadThemeFile(session, path);
  },

  async downloadFolder(prefix) {
    const { session } = get();
    if (!session) throw new Error('Sessão expirada.');
    // Empty prefix = tema inteiro. Endpoint aceita ambos.
    await downloadThemeArchive(session, prefix || undefined);
  },

  async saveAll() {
    const { session, tabs } = get();
    if (!session) return;
    const dirty = Object.values(tabs).filter((t) => t.buffer !== t.original);
    if (dirty.length === 0) return;

    set({ saveStatus: { state: 'saving' } });
    try {
      await Promise.all(dirty.map((t) => writeThemeFile(session, t.path, t.buffer)));
      set((s) => {
        const next = { ...s.tabs };
        for (const t of dirty) next[t.path] = { ...t, original: t.buffer };
        const custom = new Set(s.customized);
        for (const t of dirty) custom.add(t.path);
        return {
          tabs: next,
          customized: custom,
          saveStatus: { state: 'saved', at: Date.now() },
        };
      });
    } catch (err: any) {
      set({ saveStatus: { state: 'error', message: err.message } });
    }
  },
}));

export function isDirty(tab: Tab | undefined): boolean {
  return !!tab && tab.buffer !== tab.original;
}

/**
 * Retorna um esqueleto mínimo pra arquivo novo baseado na extensão.
 * Zero-config: user pede "Novo arquivo → header.liquid" e já abre com
 * `{% schema %}{}{% endschema %}` (evita erro de parse do engine em
 * templates de section).
 */
function boilerplateFor(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  const name = path.split('/').pop()?.replace(/\.[^.]+$/, '') || 'section';
  switch (ext) {
    case 'liquid':
      if (path.startsWith('sections/')) {
        return `{% comment %}\n  ${name}\n{% endcomment %}\n\n<div class="section-${name}">\n  \n</div>\n\n{% schema %}\n{\n  "name": "${name}",\n  "settings": []\n}\n{% endschema %}\n`;
      }
      return `{% comment %}\n  ${name}\n{% endcomment %}\n`;
    case 'json':
      return path.startsWith('templates/')
        ? `{\n  "order": [],\n  "sections": {}\n}\n`
        : `{\n  \n}\n`;
    case 'css':
    case 'scss':
      return `/* ${name} */\n`;
    case 'js':
    case 'ts':
      return `// ${name}\n`;
    case 'html':
      return `<!DOCTYPE html>\n<html>\n<head>\n  <title>${name}</title>\n</head>\n<body>\n  \n</body>\n</html>\n`;
    default:
      return '';
  }
}

/**
 * Lê um File binário como string base64 (sem o prefixo `data:X;base64,`).
 * Usado no upload de imagens/fontes pro tema.
 */
async function fileToBase64(file: File): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}
